import type { MetaFunction } from 'react-router'
import { Link } from 'react-router'
import { SEO_PAGE_HEADERS } from '~/lib/cache.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { FloatingCTA } from '~/components/floating-cta'
import { InlineLeadCapture } from '~/components/inline-lead-capture'
import { TrustStrip } from '~/components/trust-strip'
import { BudgetEstimator } from '~/components/budget-estimator'
import { ArticleSchema, ArticleMeta } from '~/components/seo-content'
import { FAQSchema, BreadcrumbSchema, StructuredData } from '~/components/structured-data'
import { ProgressivePicture } from '~/components/progressive-picture'
import { ArrowRight, CheckCircle, Calculator } from 'lucide-react'

export function headers() { return SEO_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Budget travaux 2026 — Calculateur au m² gratuit' },
  { name: 'description', content: 'Estimez votre budget travaux au m² en 2 min : tarifs 2026 par chantier (rafraîchissement → rénovation lourde) et région. Calcul gratuit + 3 devis comparatifs.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/budget-travaux' },
  { property: 'og:title', content: 'Budget travaux 2026 — Calculateur au m²' },
  { property: 'og:description', content: 'Estimation gratuite du budget travaux selon surface, type et région. Tarifs 2026 actualisés + 3 devis sous 48h.' },
  { property: 'og:type', content: 'website' },
  { property: 'og:url', content: 'https://www.afficheservices.fr/budget-travaux' },
]

const FAQS = [
  {
    question: 'Combien coûte une rénovation au m² en 2026 ?',
    answer: 'Les tarifs varient selon le type de chantier : rafraîchissement 250–500 €/m², rénovation partielle 500–1 000 €/m², rénovation complète 1 000–1 800 €/m², rénovation lourde 1 800–2 800 €/m². Les prix parisiens sont 25 % plus élevés que la moyenne nationale.',
  },
  {
    question: 'Quel budget prévoir pour rénover un appartement de 60 m² ?',
    answer: 'Pour un appartement de 60 m² en rénovation complète, comptez 60 000 à 108 000 € à Paris (1 250–2 250 €/m² majoré région), 48 000 à 86 000 € en province. Ce budget couvre tous les corps d\'état : électricité, plomberie, sols, peinture, cuisine et salle de bain.',
  },
  {
    question: 'Quelles aides financières peuvent réduire le budget travaux ?',
    answer: 'MaPrimeRénov\' couvre 30 à 90 % des travaux énergétiques selon les revenus, les CEE financent 10 à 25 % des isolations et systèmes de chauffage performants, l\'éco-PTZ permet d\'emprunter jusqu\'à 50 000 € à taux zéro. La TVA est réduite à 5,5 % sur les travaux énergétiques et 10 % sur les rénovations courantes.',
  },
  {
    question: 'Pourquoi les prix sont-ils plus élevés à Paris et en Île-de-France ?',
    answer: 'Les artisans parisiens facturent 15 à 25 % plus cher en moyenne, en raison du coût de la vie, des contraintes logistiques (stationnement, accès aux étages, copropriétés haussmanniennes), et de la forte demande qui sature les agendas. Les villes moyennes et zones rurales sont 5 à 15 % moins chères que la moyenne nationale.',
  },
  {
    question: 'Comment éviter de payer trop cher pour mes travaux ?',
    answer: 'Comparez systématiquement 3 devis pour le même chantier — les écarts atteignent 40 % entre artisans. Vérifiez que chaque devis détaille la main d\'œuvre, les matériaux (avec marque et référence) et la durée. Privilégiez les artisans certifiés (Qualibat, RGE) pour les travaux énergétiques afin de bénéficier des aides. Évitez les devis incomplets ou avec une ligne « fournitures » sans détail.',
  },
  {
    question: 'Quelle marge prévoir pour les imprévus ?',
    answer: 'Comptez 10 à 15 % de marge sur le budget initial pour les imprévus. Les surprises courantes : amiante ou plomb dans les logements anciens, remplacement de canalisations encastrées, mise aux normes électriques obligatoire, réfection de planchers détériorés. Pour les rénovations lourdes en immeuble haussmannien, prévoyez 20 % de marge.',
  },
  {
    question: 'Faut-il prévoir un architecte ou un maître d\'œuvre ?',
    answer: 'Architecte obligatoire pour : extensions au-delà de 150 m² total bâti, modification des structures porteuses, ravalement en zone protégée. Honoraires 8-12 % du montant des travaux. Pour les rénovations courantes, un maître d\'œuvre suffit (3-8 % du budget) et coordonne tous les corps d\'état sans conflits de planning. Pour les chantiers inférieurs à 50 000 €, vous pouvez piloter vous-même via une plateforme comme AfficheServices qui propose une médiation gratuite en cas de litige.',
  },
  {
    question: 'Comment se répartit le budget entre les différents corps d\'état ?',
    answer: 'Pour une rénovation complète, la répartition typique est : plomberie + sanitaires 15 %, électricité + tableau 15 %, cuisine équipée 18 %, salle de bain 12 %, peinture + plâtrerie 15 %, sols (parquet/carrelage) 15 %, menuiserie + portes 10 %. Cette répartition varie selon les choix : une cuisine haut de gamme peut représenter 25-30 % du budget total, des sanitaires premium 15-20 %.',
  },
  {
    question: 'Mon devis est-il dans la moyenne du marché ?',
    answer: 'Comparez votre devis à notre fourchette par m² selon le type de chantier. Si votre devis dépasse la fourchette haute de 30 %, demandez le détail poste par poste : tarif horaire main-d\'œuvre (45-90 €/h selon métier et région), prix des matériaux (avec marque et référence), durée d\'intervention. Les écarts entre 3 devis pour le même chantier atteignent fréquemment 30 à 40 %. Privilégiez le devis médian, pas systématiquement le moins cher.',
  },
]

const SCOPES = [
  { range: '250–500 €/m²', label: 'Rafraîchissement', what: 'Peinture, sols, finitions légères' },
  { range: '500–1 000 €/m²', label: 'Rénovation partielle', what: 'Cuisine ou salle de bain + sols + peinture' },
  { range: '1 000–1 800 €/m²', label: 'Rénovation complète', what: 'Tous corps d\'état (élec, plomberie, sols, peinture, SDB, cuisine)' },
  { range: '1 800–2 800 €/m²', label: 'Rénovation lourde', what: 'Restructuration, ouvertures de murs, isolation complète' },
]

export default function BudgetTravauxPage() {
  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Budget travaux', url: 'https://www.afficheservices.fr/budget-travaux' },
      ]} />
      <ArticleSchema
        title="Budget travaux 2026 — Calculateur prix au m²"
        description="Calculateur gratuit pour estimer votre budget rénovation au m² selon surface, type de chantier et région."
        url="https://www.afficheservices.fr/budget-travaux"
      />
      <FAQSchema items={FAQS} />
      <StructuredData data={{
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'Calculateur de budget travaux AfficheServices',
        url: 'https://www.afficheservices.fr/budget-travaux',
        applicationCategory: 'FinanceApplication',
        operatingSystem: 'All (web-based)',
        browserRequirements: 'Requires JavaScript',
        description: 'Calculateur gratuit du budget rénovation au m² selon surface, type de chantier et région. Données 2026 actualisées.',
        offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
        featureList: [
          'Estimation budget par m² (250–2 800 €/m²)',
          '4 types de chantier : rafraîchissement, partielle, complète, lourde',
          '5 zones géographiques avec multiplicateurs régionaux',
          'Répartition du budget par corps d\'état',
          'Lien direct vers 3 devis gratuits comparatifs',
        ],
      }} />

      <div className="mx-auto max-w-4xl px-4 py-10 lg:px-6">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-blue-600 p-8 text-white">
          <div className="pointer-events-none absolute inset-0 opacity-15">
            <ProgressivePicture
              baseUrl="https://images.unsplash.com/photo-1581094794329-c8112a89af12"
              alt="Chantier de rénovation appartement avec outils et plans"
              width={1200}
              height={400}
              priority
              sizes="(max-width: 768px) 100vw, 1024px"
              className="h-full"
            />
          </div>
          <div className="relative">
            <div className="flex items-center gap-2 text-sm text-blue-200">
              <Calculator className="h-4 w-4" /> Outil gratuit · Estimation 2026
            </div>
            <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Budget travaux au m² 2026 : calculateur gratuit + méthodologie</h1>
            <p className="mt-3 text-sm leading-relaxed text-blue-100 sm:text-base">
              Le budget travaux moyen au m² en France en 2026 varie de 250 €/m² (rafraîchissement)
              à 2 800 €/m² (rénovation lourde), avec une majoration de 15 à 25 % en région parisienne.
              Estimez votre projet en 2 minutes selon la surface, le type de chantier et votre région.
              Données actualisées 2026, exemples chiffrés et aides financières incluses.
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-xs text-blue-100">
              <span className="inline-flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Résultat instantané</span>
              <span className="inline-flex items-center gap-1"><CheckCircle className="h-3 w-3" /> 100 % gratuit</span>
              <span className="inline-flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Sans inscription</span>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <ArticleMeta />
        </div>

        <div className="mt-6">
          <TrustStrip />
        </div>

        <div className="mt-6">
          <InlineLeadCapture
            heading="Estimez votre projet en 2 minutes"
            subheading="Code postal → 3 devis gratuits d'artisans vérifiés"
          />
        </div>

        {/* À retenir — Key Takeaways for AI search optimization */}
        <section className="mt-6 rounded-2xl border-2 border-primary-200 bg-primary-50/40 p-6">
          <h2 className="text-base font-bold uppercase tracking-wider text-primary">À retenir</h2>
          <ul className="mt-3 space-y-2 text-sm leading-relaxed text-slate-700">
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> 4 fourchettes de prix selon l'ampleur : rafraîchissement 250-500 €/m², rénovation partielle 500-1 000 €/m², rénovation complète 1 000-1 800 €/m², rénovation lourde 1 800-2 800 €/m².</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Majoration régionale : Paris +25 %, Île-de-France +15 %, grandes métropoles +10 %, zones rurales -10 %.</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> MaPrimeRénov' + CEE + éco-PTZ peuvent couvrir 30 à 60 % des travaux énergétiques pour les ménages éligibles.</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Marge d'imprévus : 10-15 % pour les rénovations courantes, 20 % pour les immeubles haussmanniens ou logements de plus de 1949.</li>
            <li className="flex gap-2"><CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> Comparer 3 devis est le minimum pour fixer un prix juste — les écarts entre artisans atteignent fréquemment 30 à 40 %.</li>
          </ul>
        </section>

        {/* Tool */}
        <div className="mt-6">
          <BudgetEstimator />
        </div>

        {/* Visual context — breakdown by trade */}
        <div className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xs">
          <ProgressivePicture
            baseUrl="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136"
            alt="Plan de répartition du budget rénovation par corps d'état (cuisine, salle de bain, plomberie, électricité)"
            width={1200}
            height={400}
            sizes="(max-width: 768px) 100vw, 1024px"
            className="h-48 sm:h-56"
          />
          <div className="p-4 text-xs text-slate-500">
            La répartition typique d'un budget rénovation complète sur tous les corps d'état
          </div>
        </div>

        {/* Reference table */}
        <section className="mt-10 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Combien coûte une rénovation au m² en France en 2026 ?</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Les tarifs au m² varient principalement selon l'ampleur du chantier. Voici les fourchettes
            constatées en 2026, hors aides financières (MaPrimeRénov', CEE, éco-PTZ) qui peuvent
            couvrir jusqu'à 60 % des chantiers énergétiques.
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="py-2 pr-4">Type de chantier</th>
                  <th className="py-2 pr-4">Tarif moyen</th>
                  <th className="py-2">Périmètre des travaux</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {SCOPES.map((s) => (
                  <tr key={s.label}>
                    <td className="py-3 pr-4 font-semibold text-slate-900">{s.label}</td>
                    <td className="py-3 pr-4 font-medium text-primary">{s.range}</td>
                    <td className="py-3 text-slate-600">{s.what}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            * Tarifs France métropolitaine 2026. Majoration de 15 à 25 % en région parisienne. Réduction de 5 à 15 % en zone rurale.
          </p>
        </section>

        {/* Methodology — how the calculator works */}
        <section className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Comment fonctionne notre méthode de calcul ?</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            Le budget estimé combine quatre variables qui couvrent 90 % de la variation de prix observée
            sur le marché français des travaux résidentiels en 2026.
          </p>
          <ol className="mt-4 space-y-3 text-sm text-slate-700">
            <li>
              <strong className="text-slate-900">1. Surface (m²)</strong> — Multiplicateur direct sur le coût total. Une rénovation complète sur 60 m²
              coûte environ 1,5× moins qu'une rénovation identique sur 90 m² à cause des économies d'échelle (mobilisation chantier, déplacements artisans).
            </li>
            <li>
              <strong className="text-slate-900">2. Type de chantier</strong> — 4 niveaux d'ampleur définissent la fourchette €/m² :
              rafraîchissement (peinture + sols), rénovation partielle (cuisine OU SDB), rénovation complète (tous corps d'état),
              rénovation lourde (modifications structurelles).
            </li>
            <li>
              <strong className="text-slate-900">3. Région</strong> — Multiplicateur géographique appliqué : Paris ×1,25, Île-de-France ×1,15,
              grandes métropoles ×1,10, villes moyennes ×1,00, rural ×0,90. Reflète le coût horaire des artisans
              et les contraintes logistiques (stationnement, accès).
            </li>
            <li>
              <strong className="text-slate-900">4. Type de bien</strong> — Maison ×1,05 vs appartement ×1,00. Surcoût maison lié aux interventions
              extérieures (toiture, façade, terrain) et à la gestion des accès.
            </li>
          </ol>
          <p className="mt-4 text-xs text-slate-500">
            Limitation honnête : nos benchmarks sont des moyennes France 2026 (sources ADEME, ANAH, Capeb).
            Pour un chiffrage précis intégrant votre projet (état du bien, accessibilité, matériaux choisis),
            comparez 3 devis réels d'artisans vérifiés.
          </p>
        </section>

        {/* 3 worked examples — concrete mini-stories */}
        <section className="mt-8">
          <h2 className="text-xl font-bold text-slate-900">3 exemples de calcul concrets</h2>
          <div className="mt-6 space-y-5">
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 1 : T3 Paris à rafraîchir</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                <strong>Léa et Marc</strong>, 32 ans, viennent d'acheter un T3 de 65 m² dans le 11e arrondissement.
                État correct, sols et peinture à refaire, électricité aux normes. Calcul rafraîchissement Paris :
                65 m² × 375 €/m² (médiane 250-500) × 1,25 (Paris) × 1,00 (appartement) = <strong>30 469 €</strong>.
                Répartition : 50 % peinture (15 234 €), 30 % sols (9 141 €), 20 % petites finitions (6 094 €).
                Aucune aide énergétique ne s'applique pour ce type de chantier — paiement direct.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 2 : maison 110 m² rénovation complète Lyon</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                <strong>Famille Dubois</strong>, 4 personnes, maison de 1985 à Lyon. Rénovation complète : nouveau tableau électrique,
                refait la plomberie, isolation des combles, remplacement chaudière par PAC air/eau, cuisine et SDB neuves.
                Calcul : 110 m² × 1 400 €/m² (médiane 1 000-1 800) × 1,10 (métropole) × 1,05 (maison) = <strong>177 870 €</strong>.
                Avec MaPrimeRénov' Jaune (4 000 € PAC) + CEE (5 000 €) + éco-PTZ : reste à charge réel ~168 870 €,
                finançable sur 20 ans à taux zéro pour la part éligible.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-white p-6 shadow-xs">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Cas 3 : appartement haussmannien lourde rénovation</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-700">
                <strong>Karim et Sophia</strong>, T4 haussmannien 95 m² à Bordeaux. Restructuration : ouverture mur porteur (étude structure 1 200 €),
                isolation par l'intérieur, mise aux normes électriques complète, parquet d'origine restauré, salles d'eau neuves.
                Calcul rénovation lourde : 95 m² × 2 300 €/m² × 1,10 (métropole) × 1,00 = <strong>240 350 €</strong>.
                Marge imprévus haussmannien à 20 % : prévoir <strong>288 420 €</strong> total.
                Aides : MaPrimeRénov' parcours accompagné (gain 3 classes DPE attendu) + CEE bonifiés.
              </p>
            </div>
          </div>
        </section>

        {/* Contextual narrative — for AI citation depth */}
        <section className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-6">
          <h2 className="text-lg font-bold text-slate-900">Comment réduire la facture grâce aux aides 2026 ?</h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-700">
            Avant de lancer le chantier, vérifiez votre éligibilité aux trois principaux dispositifs
            cumulables : <strong>MaPrimeRénov'</strong> (jusqu'à 90 % des travaux énergétiques selon les
            revenus), les <strong>Certificats d'Économies d'Énergie (CEE)</strong> (10 à 25 % des isolations
            et systèmes de chauffage performants), et l'<strong>éco-PTZ</strong> (jusqu'à 50 000 € sans intérêts).
            La <strong>TVA réduite à 5,5 %</strong> s'applique aux travaux d'amélioration énergétique dans les
            logements de plus de 2 ans, et la <strong>TVA à 10 %</strong> couvre les rénovations courantes.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Link to="/maprimenov" className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
              Simulateur MaPrimeRénov' →
            </Link>
            <Link to="/cee" className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
              Catalogue primes CEE →
            </Link>
            <Link to="/simulateur-aides" className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100">
              Simulateur global d'aides →
            </Link>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Questions fréquentes</h2>
          <div className="mt-4 space-y-4">
            {FAQS.map((f, i) => (
              <details key={i} className="group border-b border-slate-100 pb-4 last:border-0">
                <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900 hover:text-primary">
                  {f.question}
                  <span className="text-slate-300 group-open:rotate-45">+</span>
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.answer}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Related guides */}
        <section className="mt-8">
          <h2 className="text-lg font-bold text-slate-900">Guides associés</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Link to="/cout/renovation-salle-de-bain" className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary hover:bg-primary-50">
              <p className="text-xs font-semibold text-slate-500">Coût détaillé</p>
              <p className="mt-1 text-sm font-bold text-slate-900">Rénovation salle de bain</p>
            </Link>
            <Link to="/cout/renovation-cuisine" className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary hover:bg-primary-50">
              <p className="text-xs font-semibold text-slate-500">Coût détaillé</p>
              <p className="mt-1 text-sm font-bold text-slate-900">Rénovation cuisine</p>
            </Link>
            <Link to="/cout/renovation-appartement-m2" className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary hover:bg-primary-50">
              <p className="text-xs font-semibold text-slate-500">Coût détaillé</p>
              <p className="mt-1 text-sm font-bold text-slate-900">Rénovation appartement au m²</p>
            </Link>
            <Link to="/calculateur" className="rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-primary hover:bg-primary-50">
              <p className="text-xs font-semibold text-slate-500">Outil</p>
              <p className="mt-1 text-sm font-bold text-slate-900">Calculateur prix par service</p>
            </Link>
          </div>
        </section>

        {/* Final CTA */}
        <section className="mt-8 rounded-2xl bg-primary-50 p-8 text-center">
          <h2 className="text-lg font-bold text-slate-900">Prêt à lancer votre projet ?</h2>
          <p className="mt-2 text-sm text-slate-600">Recevez 3 devis gratuits d'artisans vérifiés sous 48h, sans engagement.</p>
          <Link to="/devis-gratuit" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press">
            Demander 3 devis gratuits <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <FloatingCTA label="3 devis pour ce projet" subtitle="Comparez et économisez 30 %" whatsappMessage="Bonjour, je souhaite estimer le budget de mon projet et recevoir 3 devis." />
      <Footer />
    </div>
  )
}
